import { Link } from 'react-router-dom';
import { BookOpen, Compass, ShieldCheck, Sparkles } from 'lucide-react';
import PageMascots from '../components/PageMascots';

const TRAVELLER_FAQ = [
  {
    q: 'What do I book on Ghostie?',
    a: 'You book time with a vetted local host who shares your curiosity for folklore, mysterious places, strange cafés, hidden corners, ghost stories, local legends and atmospheric neighbourhoods.',
  },
  {
    q: 'Can I join as a solo traveller?',
    a: 'Yes. Ghostie is especially friendly for solo travellers who want something social, safe and memorable to do in a city without spending the whole day alone.',
  },
  {
    q: 'How is my local Ghostie matched?',
    a: 'After you pick a city, date and vibe, we match you with an available local whose interests fit what you want to explore: folklore, dark history, witchy corners, cemeteries, cafés, markets or local oddities.',
  },
  {
    q: 'What if a city does not have an approved local yet?',
    a: 'You can still request the experience. If we need extra time to confirm the right person, the first available dates will start later so we can protect the quality of the match.',
  },
];

const HOST_FAQ = [
  {
    q: 'Who can become a Ghostie host?',
    a: 'People who genuinely know and love the strange side of their city: folklore fans, dark history nerds, café hunters, cemetery walkers, occult-symbol spotters, local storytellers and curious humans with good social energy.',
  },
  {
    q: 'Do I need to be a professional guide?',
    a: 'No. Ghostie is built around local personality, curiosity and connection. You should be reliable, thoughtful, safe and able to create a warm experience, but you do not need to perform like a lecturer.',
  },
  {
    q: 'Why is there an introductory call?',
    a: 'The call helps us understand your city knowledge, communication style, safety awareness and the kind of traveller you would be best matched with.',
  },
  {
    q: 'How do host payments work?',
    a: 'Once approved, hosts can add their preferred payout details in the dashboard. The available payout method can depend on the country selected.',
  },
];

const GENERAL = [
  {
    title: 'Terms',
    text: 'The terms explain how bookings, accounts, payments, responsibilities and platform rules work.',
    link: '/legal/terms',
  },
  {
    title: 'Privacy',
    text: 'The privacy page explains how Ghostie handles account details, booking data, messages and essential platform information.',
    link: '/legal/privacy',
  },
  {
    title: 'Guidelines',
    text: 'Safety and host guidelines explain how travellers and locals should behave before, during and after a meetup.',
    link: '/legal/safety',
  },
  {
    title: 'Cancellation',
    text: 'The cancellation policy explains what happens if a booking needs to be changed, cancelled or reviewed.',
    link: '/legal/cancellation',
  },
  {
    title: 'Paranormal disclaimer',
    text: 'Ghostie celebrates folklore, mystery and atmosphere, but supernatural activity is never guaranteed.',
    link: '/legal/paranormal',
  },
];

function FAQList({ items }) {
  return (
    <div className="divide-y divide-[#3f2e1e]">
      {items.map((item) => (
        <details key={item.q} className="group py-5">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-heading text-2xl">
            <span>{item.q}</span>
            <span className="text-[#f97316] transition-transform group-open:rotate-45">+</span>
          </summary>
          <p className="mt-3 max-w-3xl text-[#c7c7c7] leading-relaxed">{item.a}</p>
        </details>
      ))}
    </div>
  );
}

export default function FAQ() {
  return (
    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 overflow-visible">
      <PageMascots />
      <div className="relative z-10">
      <section className="grid lg:grid-cols-[0.85fr_1.15fr] gap-10 items-end mb-16">
        <div>
          <div className="badge-cozy mb-4">FAQ</div>
          <h1 className="text-5xl sm:text-7xl font-heading font-semibold leading-none">
            Answers before you follow the strange.
          </h1>
        </div>
        <p className="text-[#c7c7c7] leading-relaxed text-lg border-l border-[#3f2e1e] pl-6">
          A clear, candlelit little hub for travellers, hosts and anyone checking the practical bits before booking, applying or collaborating with Ghostie.
        </p>
      </section>

      <section className="py-14 border-y border-[#3f2e1e]">
        <div className="flex items-center gap-3 mb-8">
          <Compass className="text-[#f97316]" />
          <h2 className="font-heading text-4xl">For travellers</h2>
        </div>
        <FAQList items={TRAVELLER_FAQ} />
      </section>

      <section className="py-14 border-b border-[#3f2e1e]">
        <div className="flex items-center gap-3 mb-8">
          <Sparkles className="text-[#f97316]" />
          <h2 className="font-heading text-4xl">For hosts</h2>
        </div>
        <FAQList items={HOST_FAQ} />
      </section>

      <section className="py-14">
        <div className="flex items-center gap-3 mb-8">
          <ShieldCheck className="text-[#f97316]" />
          <h2 className="font-heading text-4xl">General policies</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {GENERAL.map((item) => (
            <Link key={item.title} to={item.link} className="magical-card p-6 hover:border-[#f97316] transition-colors">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen size={16} className="text-[#f97316]" />
                <h3 className="font-heading text-2xl">{item.title}</h3>
              </div>
              <p className="text-sm text-[#a3a3a3] leading-relaxed">{item.text}</p>
            </Link>
          ))}
        </div>
      </section>
      </div>
    </div>
  );
}
